import type { Game, GameData, GameDataV2 } from '@/types/games';
import type { PrismicImage, PrismicLink } from '@/types/prismic/common';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';

type GridLayoutCtx = {
    layout: PRISMIC_ZONE_LAYOUT;
    position: number;
};

export const getGameName = (displayName?: string | Array<{ text?: string }>) => {
    if (!displayName) {
        return '';
    }

    if (typeof displayName === 'string') {
        return displayName;
    }
    return displayName?.[0]?.text || '';
};

export const getProviderName = (providerName?: string | Array<{ text?: string }>) => {
    if (!providerName) {
        return '';
    }

    if (typeof providerName === 'string') {
        return providerName;
    }
    return providerName?.[0]?.text || '';
};

export const getGameStudioName = (
    displayName?: string | Array<{ text?: string }> | { text?: string } | null
): string => {
    if (!displayName) {
        return '';
    }
    if (typeof displayName === 'string') {
        return displayName;
    }
    if (Array.isArray(displayName)) {
        return displayName[0]?.text ?? '';
    }
    return displayName.text ?? '';
};

export const getImageUrl = (image?: string | Array<{ image: PrismicImage } | { image?: { url?: string } }>): string => {
    if (!image || !image.length) {
        return '';
    }

    if (typeof image === 'string') {
        return image;
    }
    return image?.[0]?.image?.url || '';
};

export const getImageSmallUrl = (image?: string | { image: PrismicImage }[]): string => {
    if (!image || !image.length) {
        return '';
    }
    if (typeof image === 'string') {
        return `${image}&w=225&h=225`;
    }

    return image?.[0]?.image?.small?.url || '';
};

export const getImageMediumUrl = (image?: string | { image: PrismicImage }[]): string => {
    if (!image || !image.length) {
        return '';
    }

    if (typeof image === 'string') {
        return `${image}&w=450&h=450`;
    }
    return image?.[0]?.image?.medium?.url || '';
};

export const getVideoUrl = (video?: string | { url?: string }): string => {
    if (typeof video === 'string') {
        return video;
    }
    return video?.url || '';
};

export const setGameFilters = (
    game: Game,
    themes: Set<string>,
    features: Set<string>,
    genres: Set<string>,
    studios: Set<string>
) => {
    if (!game.data) {
        return;
    }
    if ('metadata' in game.data && game.data.metadata) {
        const gameData = game.data as GameDataV2;
        gameData.metadata.forEach((item) => {
            if (item.startsWith('theme-')) {
                themes.add(item);
            } else if (item.startsWith('feature-')) {
                features.add(item);
            } else if (item.startsWith('genre-')) {
                genres.add(item);
            }
        });

        if (gameData.game_studio?.uid) {
            studios.add(gameData.game_studio?.uid);
        }
    } else {
        const gameData = game.data as GameData;
        gameData.themes?.forEach((f: any) => {
            if (f?.theme?.uid) themes.add(f.theme.uid);
        });
        gameData.features?.forEach((f: any) => {
            if (f?.feature?.uid) features.add(f.feature.uid);
        });
        gameData.genres?.forEach((f: any) => {
            if (f?.genre?.uid) genres.add(f.genre.uid);
        });

        if (gameData?.game_studio?.uid) {
            studios.add(gameData?.game_studio?.uid);
        }
    }
};

export const getGameFilters = (game: Game) => {
    if (!game.data) {
        return;
    }
    if ('metadata' in game.data && game.data.metadata) {
        const gameData = game.data as GameDataV2;
        // Keep full strings (e.g. "theme-action-and-adventure") so they match filter option uids from setGameFilters.
        const gameThemes: string[] = gameData.metadata.filter((item) => item.startsWith('theme-'));
        const gameFeatures: string[] = gameData.metadata.filter((item) => item.startsWith('feature-'));
        const gameGenres: string[] = gameData.metadata.filter((item) => item.startsWith('genre-'));

        return {
            themes: gameThemes,
            features: gameFeatures,
            genres: gameGenres,
            studio: gameData.game_studio?.uid
        };
    }
    const gameData = game.data as GameData;
    const gameThemes: string[] = gameData.themes?.map((g: any) => g?.theme?.uid)?.filter(Boolean) ?? [];
    const gameFeatures: string[] = gameData.features?.map((f: any) => f?.feature?.uid)?.filter(Boolean) ?? [];
    const gameGenres: string[] = gameData.genres?.map((g: any) => g?.genre?.uid)?.filter(Boolean) ?? [];

    return {
        themes: gameThemes,
        features: gameFeatures,
        genres: gameGenres,
        studio: gameData.game_studio?.uid
    };
};

export const getGameInfoModalMetadata = (game: Game) => {
    if (!game.data) {
        return {
            genreUid: '',
            volatility: '',
            themes: [],
            features: [],
            studioName: ''
        };
    }
    const studioName = getGameStudioName(game.data.game_studio?.data?.display_name);
    if ('metadata' in game.data && game.data.metadata) {
        const gameData = game.data as GameDataV2;

        const { genreUid, volatility, themes, features } = gameData.metadata.reduce(
            (acc, item: string) => {
                if (item.startsWith('genre-') && !acc.genreUid) {
                    acc.genreUid = item;
                } else if (item.startsWith('volatility-') && !acc.volatility) {
                    acc.volatility = item;
                } else if (item.startsWith('theme-')) {
                    acc.themes.push({ theme: { uid: item } });
                } else if (item.startsWith('feature-')) {
                    acc.features.push({ feature: { uid: item } });
                }
                return acc;
            },
            {
                genreUid: '',
                volatility: '',
                themes: [] as { theme: { uid: string } }[],
                features: [] as { feature: { uid: string } }[]
            }
        );

        return {
            genreUid,
            volatility,
            themes,
            features,
            studioName
        };
    }
    const gameData = game.data as GameData;
    return {
        genreUid: gameData?.genres?.[0]?.genre?.uid || '',
        volatility: gameData?.volatility || '',
        themes: gameData?.themes || [],
        features: gameData?.features || [],
        studioName
    };
};

export const getGameAnimatedTileUrl = (
    assets: Game['data']['animated_tiles_assets'],
    gridLayoutCtx?: GridLayoutCtx
): string | null => {
    const urlFromLink = (link: PrismicLink | undefined | null) => {
        const u = link?.url?.trim();
        return u && u.length > 0 ? u : null;
    };

    const asset = assets?.[0];
    if (!asset?.enabled) return null;
    if (!gridLayoutCtx) return urlFromLink(asset.default_layout_url);

    const { layout, position } = gridLayoutCtx;

    //TODO uncomment when applied in v2
    // if (layout === PRISMIC_ZONE_LAYOUT.TALL) return urlFromLink(asset.tall_portrait_url);
    if (layout === PRISMIC_ZONE_LAYOUT.TALL) return null;

    if ([PRISMIC_ZONE_LAYOUT.SPOTLIGHT, PRISMIC_ZONE_LAYOUT.VERTICAL_FEATURED_GRID].includes(layout)) {
        const isLandscapeSlot = position !== 1 && (position - 2) % 4 >= 2;
        //TODO uncomment when applied in v2
        // if (isLandscapeSlot) return urlFromLink(asset.landscape_url);
        if (isLandscapeSlot) return null;
        return urlFromLink(asset.default_layout_url);
    }

    return urlFromLink(asset.default_layout_url);
};
